import { CalendarOutlined, DownloadOutlined } from "@ant-design/icons";
import { App, Button, DatePicker, Form, Space } from "antd";
import dayjs from "dayjs";
import { type ComponentProps, useState } from "react";
import { AttendanceTable } from "@/modules/daily-attendance/components/AttendanceTable";
import { dailyAttendanceService } from "@/modules/daily-attendance/services/daily-attendance.service";
import { DEFAULT_DATE_RANGE } from "@/modules/daily-attendance/utils/attendance.constants";
import { useOrganization } from "@/modules/organizations/hooks/useOrganizations";

interface OrgAttendanceReportTabProps {
	organizationId: string;
}

type RangePickerOnChange = NonNullable<ComponentProps<typeof DatePicker.RangePicker>["onChange"]>;

export function OrgAttendanceReportTab({ organizationId }: OrgAttendanceReportTabProps) {
	const { message } = App.useApp();
	const { data: org } = useOrganization(organizationId);
	const organizationName = org?.name ?? organizationId;
	const [startDate, setStartDate] = useState<string>(DEFAULT_DATE_RANGE.start);
	const [endDate, setEndDate] = useState<string>(DEFAULT_DATE_RANGE.end);
	const [isExporting, setIsExporting] = useState(false);

	const rangeValue: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs(startDate), dayjs(endDate)];

	const handleRangeChange: RangePickerOnChange = (dates) => {
		if (dates?.[0] && dates?.[1]) {
			setStartDate(dates[0].format("YYYY-MM-DD"));
			setEndDate(dates[1].format("YYYY-MM-DD"));
		}
	};

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const blob = await dailyAttendanceService.exportAttendanceReport({
				organizationId,
				startDate,
				endDate,
				format: "xlsx",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `davomat-${organizationName}-${startDate}-${endDate}.xlsx`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch {
			message.error("Eksport muvaffaqiyatsiz tugadi");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div>
			<Form layout="inline" className="mb-4 flex flex-wrap gap-2">
				<Form.Item
					label={
						<Space size={4}>
							<CalendarOutlined />
							<span>Davr</span>
						</Space>
					}
					className="mb-2"
				>
					<DatePicker.RangePicker
						allowClear={false}
						value={rangeValue}
						onChange={handleRangeChange}
						placeholder={["Boshlanish", "Tugash"]}
						format="DD.MM.YYYY"
						style={{ width: 280 }}
					/>
				</Form.Item>

				<Form.Item label=" " className="mb-2">
					<Button
						type="primary"
						icon={<DownloadOutlined />}
						onClick={handleExport}
						loading={isExporting}
						disabled={!(organizationId && startDate && endDate)}
					>
						Excel
					</Button>
				</Form.Item>
			</Form>

			<AttendanceTable
				organizationId={organizationId}
				organizationName={organizationName}
				startDate={startDate}
				endDate={endDate}
			/>
		</div>
	);
}
